import React from 'react';
import { notification } from 'antd';
import styled from 'styled-components';
import { CheckCircleFilled, ExclamationCircleFilled, InfoCircleFilled, StopFilled } from '@ant-design/icons';
import { ArgsProps } from 'antd/lib/notification';
import i18n from '@app/i18n';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { resolveTranslation } from '@app/utils/translateI18nMessage';
import { FONT_SIZE } from '@app/styles/themes/constants';

interface IconWrapperProps {
  $isOnlyTitle: boolean;
}

const IconWrapper = styled.div<IconWrapperProps>`
  font-size: ${(props) => (props.$isOnlyTitle ? '2rem' : '3rem')};
  line-height: 2rem;
`;

const EmptyDescription = styled.div`
  margin-top: -0.75rem;
`;

const CopyButton = styled(BaseButton)`
  &&& {
    position: absolute;
    top: 1.25rem;
    right: calc(1.25rem + 22px + 0.25rem);
    height: 22px;
    padding: 0 0.375rem;
    font-size: ${FONT_SIZE.xs};
    line-height: 22px;
    color: #404040;
  }
`;

interface NotificationProps extends ArgsProps {
  /** Shows a copy button that puts this text on the clipboard, e.g. an error reference id. */
  copyText?: string;
}

type NotificationType = 'success' | 'info' | 'warning' | 'error';

/**
 * A page that renders twice (React StrictMode in dev) or fires several requests
 * in parallel raises the exact same toast more than once. Identical toasts inside
 * this window collapse to the first one; it keeps its original timer.
 */
const DEDUPE_WINDOW_MS = 2000;

const recentNotifications = new Map<string, number>();

const isDuplicate = (type: NotificationType, config: NotificationProps): boolean => {
  const { message, description, key } = config;

  // Only plain text can be compared; a ReactNode always gets through.
  if (typeof message !== 'string' || (description !== undefined && typeof description !== 'string')) {
    return false;
  }

  const signature = `${type}\u0000${key ?? ''}\u0000${message}\u0000${description ?? ''}`;
  const now = Date.now();
  const lastShownAt = recentNotifications.get(signature);

  if (lastShownAt !== undefined && now - lastShownAt < DEDUPE_WINDOW_MS) {
    return true;
  }

  recentNotifications.forEach((timestamp, entry) => {
    if (now - timestamp >= DEDUPE_WINDOW_MS) {
      recentNotifications.delete(entry);
    }
  });
  recentNotifications.set(signature, now);

  return false;
};

const openNotification = (type: NotificationType, icon: React.ReactNode, config: NotificationProps): void => {
  if (isDuplicate(type, config)) {
    return;
  }

  const { copyText, ...antdConfig } = config;
  const copyLabel = resolveTranslation(i18n.t, 'common.copy');

  notification[type]({
    ...antdConfig,
    icon: <IconWrapper $isOnlyTitle={!config.description || !!copyText}>{icon}</IconWrapper>,
    message: (
      <>
        <div className={`title ${(!config.description || copyText) && `title-only`}`}>{config.message}</div>
        {copyText && (
          <CopyButton
            type="text"
            size="small"
            onClick={() => navigator.clipboard?.writeText(copyText).catch(() => undefined)}
          >
            {copyLabel}
          </CopyButton>
        )}
      </>
    ),
    description: config.description ? <div className="description">{config.description}</div> : <EmptyDescription />,
    className: [config.description ? '' : 'notification-without-description', copyText ? 'notification-with-copy' : '']
      .filter(Boolean)
      .join(' '),
  });
};

const openSuccessNotification = (config: NotificationProps): void => {
  openNotification('success', <CheckCircleFilled className="success-icon" />, config);
};

const openInfoNotification = (config: NotificationProps): void => {
  openNotification('info', <InfoCircleFilled className="info-icon" />, config);
};

const openWarningNotification = (config: NotificationProps): void => {
  openNotification('warning', <ExclamationCircleFilled className="warning-icon" />, config);
};

const openErrorNotification = (config: NotificationProps): void => {
  openNotification('error', <StopFilled className="error-icon" />, config);
};

export const notificationController = {
  success: openSuccessNotification,
  info: openInfoNotification,
  warning: openWarningNotification,
  error: openErrorNotification,
};
